import { motion, AnimatePresence } from 'framer-motion';
import { CSSProperties, ReactNode } from 'react';
import Button from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  maxWidth?: number | string;
}

export default function Modal({ isOpen, onClose, title, children, actions, maxWidth }: ModalProps) {
  const overlayStyles: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 'var(--spacing-md)'
  };

  const modalStyles: CSSProperties = {
    background: 'white',
    borderRadius: 'var(--radius-xl)',
    padding: 'var(--spacing-2xl)',
    maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth || '500px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    overflowX: 'visible',
    boxShadow: 'var(--shadow-xl)'
  };

  const titleStyles: CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--color-secondary)',
    marginBottom: 'var(--spacing-md)'
  };

  const contentStyles: CSSProperties = {
    color: 'var(--color-text)',
    marginBottom: 'var(--spacing-lg)'
  };

  const actionsStyles: CSSProperties = {
    display: 'flex',
    gap: 'var(--spacing-md)',
    justifyContent: 'flex-end'
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          style={overlayStyles}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            style={modalStyles}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={titleStyles}>{title}</h2>
            <div style={contentStyles}>{children}</div>
            {actions ? (
              <div style={actionsStyles}>{actions}</div>
            ) : (
              <div style={actionsStyles}>
                <Button onClick={onClose}>Close</Button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
